// @ts-nocheck -- TEMPORARY: the 'json' field type is not yet published in
// SchemaField (@kalyzee/kast-app-web-components). Remove this once the library
// exposes it, or type it locally and cast at the SettingsFormSchema boundary.
// Type-only: this module must not load the component library at runtime
// (settings.ts depends on it and is imported outside any React context).
import type { SettingsFormSchema, SchemaField } from "@kalyzee/kast-web-sdk/web-components";
import { formatJsonParseError } from "./json";
import { decodeBase64Url } from "./settings";

export const SETTINGS_SCHEMA_VERSION = 15;

// ---- PTZ sub-schema (MeetingSourceDevicePTZ) ----

const ptzSchema: SchemaField = {
  type: 'object',
  label: 'PTZ',
  properties: {
    mode: {
      type: 'select',
      label: 'Mode',
      required: true,
      options: [
        { label: 'Aver', value: 'aver' },
        { label: 'Custom', value: 'custom' },
      ],
    },
  },
  discriminator: {
    field: 'mode',
    mapping: {
      aver: {
        data: {
          type: 'object',
          label: 'Configuration Aver',
          required: true,
          properties: {
            hostname: { type: 'string', label: 'Hostname', required: true, placeholder: '192.168.1.x' },
            username: { type: 'string', label: 'Nom d\'utilisateur' },
            password: { type: 'string', label: 'Mot de passe' },
          },
        },
      },
      custom: {
        custom: { type: 'json', expect: 'object', label: 'Custom PTZ (JSON)', description: 'Objet { move, stopMove, zoom, stopZoom, loadPreset, setPreset, scene }' },
      },
    },
  },
};

// ---- MediaDeviceInfoSelector sub-schema ----
// { kind?, deviceId?, groupId?, label?, index? } — values can be string | { regex, flag? }

const mediaDeviceSelectorSchema = (label: string): SchemaField => ({
  type: 'string',
  label,
  description: 'String (deviceId) ou JSON : { "label": { "regex": "cam", "flag": "i" } }',
  multiline: true,
  placeholder: '{ "label": { "regex": "cam" } }',
});

// ---- Device data per type (discriminated by device.type) ----

const ovenMediaDataProperties: Record<string, SchemaField> = {
  url: { type: 'string', label: 'URL', required: true, placeholder: 'wss://...' },
  sdpFormatter: { type: 'string', label: 'SDP Formatter' },
  ptz: ptzSchema,
  byPassAudioEncoder: { type: 'boolean', label: 'Bypass audio encoder' },
  byPassVideoEncoder: { type: 'boolean', label: 'Bypass video encoder' },
};

const kastDataProperties: Record<string, SchemaField> = {
  ip: { type: 'string', label: 'IP', required: true, placeholder: '192.168.1.x' },
  port: { type: 'number', label: 'Port', step: 1 },
  defaultScene: { type: 'json', label: 'Scène par défaut (JSON)' },
  defaultView: { type: 'json', label: 'Vue par défaut (JSON)' },
  mediaConstraints: { type: 'json', expect: 'object', label: 'Media Constraints (JSON)', placeholder: '{ "video": true, "audio": true }' },
};

const whiteboardDataProperties: Record<string, SchemaField> = {
  ip: { type: 'string', label: 'IP', required: true, placeholder: '192.168.1.x' },
  port: { type: 'number', label: 'Port', step: 1 },
  defaultVideoSource: { type: 'string', label: 'Source vidéo par défaut', placeholder: 'screen' },
  defaultAudioSource: {
    type: 'select', label: 'Source audio par défaut',
    options: [
      { label: 'Playback', value: 'playback' },
      { label: 'Microphone', value: 'microphone' },
      { label: 'Mixed', value: 'mixed' },
    ],
  },
  defaultPreferVideoSource: { type: 'string', label: 'Source vidéo préférée', placeholder: 'screen' },
  defaultPreferAudioSource: {
    type: 'select', label: 'Source audio préférée',
    options: [
      { label: 'Playback', value: 'playback' },
      { label: 'Microphone', value: 'microphone' },
      { label: 'Mixed', value: 'mixed' },
    ],
  },
  defaultAudioPlaybackVolume: { type: 'number', label: 'Volume playback', min: 0, max: 1, step: 0.1 },
  defaultAudioMicrophoneVolume: { type: 'number', label: 'Volume micro', min: 0, max: 1, step: 0.1 },
  mediaConstraints: { type: 'json', expect: 'object', label: 'Media Constraints (JSON)', placeholder: '{ "video": true, "audio": true }' },
};

const webDataProperties: Record<string, SchemaField> = {
  disableShareScreen: { type: 'boolean', label: 'Désactiver partage d\'écran', description: 'Activé par défaut.' },
  defaultVideoDevice: mediaDeviceSelectorSchema('Device vidéo par défaut'),
  defaultAudioDevice: mediaDeviceSelectorSchema('Device audio par défaut'),
  filterVideoDevices: mediaDeviceSelectorSchema('Filtre devices vidéo'),
  filterAudioDevices: mediaDeviceSelectorSchema('Filtre devices audio'),
  excludeVideoDevices: mediaDeviceSelectorSchema('Exclure devices vidéo'),
  excludeAudioDevices: mediaDeviceSelectorSchema('Exclure devices audio'),
  hideVideoDevices: { type: 'boolean', label: 'Masquer devices vidéo' },
  hideAudioDevices: { type: 'boolean', label: 'Masquer devices audio' },
  hideVideoDevicesIfNoSeveralDevices: { type: 'boolean', label: 'Masquer vidéo si 1 seul device' },
  hideAudioDevicesIfNoSeveralDevices: { type: 'boolean', label: 'Masquer audio si 1 seul device' },
  autoSetVideoDevices: { type: 'json', expect: 'array', label: 'Auto-set vidéo (JSON)', description: 'Array de MediaDeviceInfoSelector + priority' },
  autoSetAudioDevices: { type: 'json', expect: 'array', label: 'Auto-set audio (JSON)', description: 'Array de MediaDeviceInfoSelector + priority' },
  mediaConstraints: { type: 'json', expect: 'object', label: 'Media Constraints (JSON)', placeholder: '{ "video": true, "audio": true }' },
  children: { type: 'json', expect: 'array', label: 'Children devices (JSON)', description: 'Array de MeetingSourceDeviceWebChild' },
  ptz: ptzSchema,
  keepAlwaysAudioTrackAlive: { type: 'boolean', label: 'Garder la piste audio toujours active' },
  keepAlwaysVideoTrackAlive: { type: 'boolean', label: 'Garder la piste vidéo toujours active' },
};

const emptyDataProperties: Record<string, SchemaField> = {
  backgroundColor: { type: 'string', label: 'Couleur de fond', placeholder: '#000000' },
};

const customDataProperties: Record<string, SchemaField> = {
  // custom is `any` → free-form JSON
  _raw: { type: 'json', label: 'Configuration (JSON)', placeholder: '{ }' },
};

// ---- MeetingSourceDevice ----

const meetingSourceDeviceSchema: SchemaField = {
  type: 'object',
  label: 'Device',
  properties: {
    mode: {
      type: 'select',
      label: 'Mode',
      required: true,
      options: [
        { label: 'Audio + Vidéo', value: 'audiovideo' },
        { label: 'Audio', value: 'audio' },
        { label: 'Vidéo', value: 'video' },
      ],
    },
    type: {
      type: 'select',
      label: 'Type de device',
      required: true,
      options: [
        { label: 'OvenMedia', value: 'ovenmedia' },
        { label: 'Whiteboard', value: 'whiteboard' },
        { label: 'Kast', value: 'kast' },
        { label: 'Web (navigateur)', value: 'web' },
        { label: 'Vide', value: 'empty' },
        { label: 'Custom', value: 'custom' },
      ],
    },
    disabled: { type: 'boolean', label: 'Désactivé' },
    priority: { type: 'number', label: 'Priorité', step: 1 },
    unmuted: { type: 'boolean', label: 'Non muté par défaut' },
    extra: {
      type: 'object',
      label: 'Extra',
      properties: {
        style: { type: 'json', expect: 'object', label: 'Style (JSON CSS)', placeholder: '{ }' },
      },
    },
  },
  discriminator: {
    field: 'type',
    mapping: {
      ovenmedia: { data: { type: 'object', label: 'Configuration OvenMedia', required: true, properties: ovenMediaDataProperties } },
      kast: { data: { type: 'object', label: 'Configuration Kast', required: true, properties: kastDataProperties } },
      whiteboard: { data: { type: 'object', label: 'Configuration Whiteboard', required: true, properties: whiteboardDataProperties } },
      web: { data: { type: 'object', label: 'Configuration Web', required: true, properties: webDataProperties } },
      empty: { data: { type: 'object', label: 'Configuration Empty', required: true, properties: emptyDataProperties } },
      custom: { data: { type: 'object', label: 'Configuration Custom', required: true, properties: customDataProperties } },
    },
  },
};

// ---- Emission (encoding) sub-schema, per source ----
// RTCStreamParams: what the SDK applies to the sender of a published media.

const rtcEncodingSchema = (label: string, video: boolean): SchemaField => ({
  type: 'object',
  label,
  description: 'Prioritaire sur le SDP : un x-google-min-bitrate ne pousse pas au-delà.',
  properties: {
    maxBitrate: { type: 'number', label: 'Bitrate max (bps)', min: 0, step: 1000 },
    ...(video
      ? {
        maxFramerate: { type: 'number', label: 'Framerate max (fps)', min: 0, step: 1 },
        scaleResolutionDownBy: { type: 'number', label: 'Division de la résolution', description: '1 = résolution native, 2 = moitié...', min: 1, step: 0.5 },
      }
      : {}),
    networkPriority: {
      type: 'select',
      label: 'Priorité réseau',
      options: [
        { label: 'Très basse', value: 'very-low' },
        { label: 'Basse', value: 'low' },
        { label: 'Moyenne', value: 'medium' },
        { label: 'Haute', value: 'high' },
      ],
    },
    active: { type: 'boolean', label: 'Actif', description: 'Faux : la piste est publiée mais rien n\'est envoyé.' },
  },
});

const emissionStreamParamsSchema: SchemaField = {
  type: 'object',
  label: 'Encodage (émission)',
  description: 'Fusionnés par-dessus les défauts de l\'application. Appliqués à la publication suivante.',
  properties: {
    preset: {
      type: 'select',
      label: 'Preset',
      description: 'Réglages SDK de base, complétés par les champs ci-dessous.',
      options: [
        { label: 'Visio (visage)', value: 'visio_face' },
        { label: 'Partage d\'écran', value: 'visio_screensharing' },
      ],
    },
    video: {
      type: 'object',
      label: 'Vidéo',
      properties: {
        mediaContentHint: {
          type: 'select',
          label: 'Type de contenu',
          description: 'Arbitrage netteté / fluidité fait par le navigateur.',
          options: [
            { label: 'Mouvement (fluidité)', value: 'motion' },
            { label: 'Détail (netteté)', value: 'detail' },
            { label: 'Texte', value: 'text' },
          ],
        },
        rtcParams: {
          type: 'object',
          label: 'Paramètres du sender',
          properties: {
            degradationPreference: {
              type: 'select',
              label: 'Préférence de dégradation',
              description: 'Ce que le navigateur sacrifie quand le débit ne suit plus.',
              options: [
                { label: 'Garder le framerate (fluidité)', value: 'maintain-framerate' },
                { label: 'Garder la résolution (netteté)', value: 'maintain-resolution' },
                { label: 'Équilibré', value: 'balanced' },
                { label: 'Aucune dégradation', value: 'disabled' },
              ],
            },
          },
        },
        rtcEncoding: rtcEncodingSchema('Encoding', true),
        mediaConstrains: { type: 'json', expect: 'object', label: 'Contraintes de piste (JSON)', placeholder: '{ "frameRate": 25 }' },
      },
    },
    audio: {
      type: 'object',
      label: 'Audio',
      properties: {
        mediaContentHint: {
          type: 'select',
          label: 'Type de contenu',
          options: [
            { label: 'Parole', value: 'speech' },
            { label: 'Reconnaissance vocale', value: 'speech-recognition' },
            { label: 'Musique', value: 'music' },
          ],
        },
        rtcEncoding: rtcEncodingSchema('Encoding', false),
        mediaConstrains: { type: 'json', expect: 'object', label: 'Contraintes de piste (JSON)', placeholder: '{ "channelCount": 1 }' },
      },
    },
  },
};

const emissionSessionOptionsSchema: SchemaField = {
  type: 'object',
  label: 'Session (SDP / transport)',
  description: 'Traduites par le SDK en réécritures du SDP et en politique ICE.',
  properties: {
    video: {
      type: 'object',
      label: 'Vidéo',
      properties: {
        googleBitrates: {
          type: 'object',
          label: 'Bitrates x-google',
          description: 'Écrits dans les fmtp vidéo, en kbps.',
          properties: {
            start: { type: 'number', label: 'Start (kbps)', description: 'Défaut SDK : 2000 (8000 si bypass encodeur).', min: 0, step: 100 },
            min: { type: 'number', label: 'Min (kbps)', min: 0, step: 100 },
            max: { type: 'number', label: 'Max (kbps)', description: 'Défaut SDK : aucun (20000 si bypass encodeur).', min: 0, step: 100 },
          },
        },
        content: {
          type: 'select',
          label: 'Type de contenu (a=content)',
          description: 'Ajouté au SDP local de la vidéo.',
          options: [
            { label: 'Slides', value: 'slides' },
          ],
        },
        disabled: { type: 'boolean', label: 'Retirer la vidéo', description: 'Supprime le média vidéo du SDP.' },
      },
    },
    audio: {
      type: 'object',
      label: 'Audio',
      properties: {
        mono: { type: 'boolean', label: 'Mono', description: 'Format opus mono (103). Défaut SDK : activé.' },
        dtx: { type: 'boolean', label: 'DTX', description: 'Coupe l\'émission dans les silences. Défaut SDK : désactivé.' },
        adaptivePtime: { type: 'boolean', label: 'Adaptive ptime' },
        disabled: { type: 'boolean', label: 'Retirer l\'audio', description: 'Supprime le média audio du SDP.' },
      },
    },
    transportCC: {
      type: 'object',
      label: 'Transport-CC',
      description: 'Décoché : lignes transport-cc retirées. Bug SDK : la vidéo suit la valeur audio.',
      properties: {
        audio: { type: 'boolean', label: 'Audio' },
        video: { type: 'boolean', label: 'Vidéo' },
      },
    },
    transports: {
      type: 'array',
      label: 'Transports ICE',
      description: 'TCP seul force iceTransportPolicy à `relay`.',
      items: {
        type: 'select',
        label: 'Transport',
        options: [
          { label: 'UDP', value: 'udp' },
          { label: 'TCP', value: 'tcp' },
        ],
      },
    },
  },
};

// ---- MeetingSourceExtended (Partial<>) ----

const meetingSourceSchema = (label: string): SchemaField => ({
  type: 'object',
  label,
  properties: {
    name: { type: 'string', label: 'Nom', required: true, placeholder: 'webcam, screen, ...' },
    extra: {
      type: 'object',
      label: 'Type de source',
      required: true,
      properties: {
        type: {
          type: 'select',
          label: 'Type',
          required: true,
          options: [
            { label: 'Caméra', value: 'camera' },
            { label: 'Écran', value: 'screen' },
          ],
        },
        playerFit: {
          type: 'select',
          label: 'Affichage (player fit)',
          description: 'Mode d\'affichage des players. Défaut : cover.',
          options: [
            { label: 'Cover (remplir, défaut)', value: 'cover' },
            { label: 'Contain (image entière)', value: 'contain' },
            { label: 'Fill (étirer)', value: 'fill' },
            { label: 'None', value: 'none' },
            { label: 'Scale-down', value: 'scale-down' },
            { label: 'Interactif (recadrage + minimap)', value: 'interactive' },
          ],
        },
        width: {
          type: 'number',
          label: 'Largeur (px)',
          step: 1,
        },
        height: {
          type: 'number',
          label: 'Hauteur (px)',
          step: 1,
        },
      },
    },
    devices: {
      type: 'array',
      label: 'Devices',
      required: true,
      items: meetingSourceDeviceSchema,
    },
    id: { type: 'string', label: 'ID' },
    disabled: { type: 'boolean', label: 'Désactivé' },
    hideIfNoMedia: { type: 'boolean', label: 'Masquer si pas de média' },
    priority: { type: 'number', label: 'Priorité', step: 1 },
    visible: { type: 'boolean', label: 'Visible' },
    signalDetection: {
      type: 'object',
      label: 'Détection de signal vidéo',
      description: "Masque automatiquement le flux quand la source ne renvoie plus d'image (rien de branché, mire de la carte d'acquisition), et le réaffiche au retour du signal.",
      properties: {
        enabled: { type: 'boolean', label: 'Activer la détection' },
        preset: {
          type: 'select',
          label: 'Preset',
          description: "À choisir selon ce qu'affiche la source en l'absence de signal.",
          options: [
            { label: 'Défaut (noir, mire 8 barres, image figée)', value: 'default' },
            { label: 'Mire SMPTE 8 barres', value: 'smpte-color-bars' },
            { label: 'Mire SMPTE 75 %', value: 'smpte-75-color-bars' },
            { label: 'Noir uniquement', value: 'black-only' },
            { label: 'Image figée uniquement', value: 'frozen-only' },
            { label: 'Réactif (~1 s, plus de faux positifs)', value: 'reactive' },
          ],
        },
        interval: {
          type: 'number',
          label: "Intervalle d'échantillonnage (ms)",
          description: 'Remplace la valeur du preset.',
          min: 0,
          step: 100,
        },
        noSignalConfirmations: {
          type: 'number',
          label: 'Échantillons avant masquage',
          description: "Nombre d'échantillons consécutifs sans signal avant de masquer le flux.",
          min: 1,
          step: 1,
        },
        signalConfirmations: {
          type: 'number',
          label: 'Échantillons avant réaffichage',
          description: "Nombre d'échantillons consécutifs avec signal avant de réafficher le flux.",
          min: 1,
          step: 1,
        },
      },
    },
    audioLevel: {
      type: 'object',
      label: 'Indicateur de niveau audio',
      description: "Barre de niveau affichée en bas de vos propres players pour cette source. Les volumes sont sur l'échelle 0-255 de l'analyseur, convertie depuis des décibels : une pièce silencieuse n'y vaut pas 0 mais son bruit de fond, c'est donc le volume min qui dit où commence le silence.",
      properties: {
        disabled: { type: 'boolean', label: 'Désactivé' },
        boost: { type: 'number', label: 'Boost', description: 'Exposant appliqué au niveau. Défaut : 2.', min: 0, step: 0.1 },
        minVolume: { type: 'number', label: 'Volume min', description: 'Niveau lu comme silence. Défaut : 0.', min: 0, max: 255, step: 1 },
        maxVolume: { type: 'number', label: 'Volume max', description: 'Niveau lu comme pleine échelle. Défaut : 220.', min: 0, max: 255, step: 1 },
        displayNoSoundWarning: { type: 'boolean', label: 'Avertir en cas de silence', description: 'Tant que la source est restée sous le volume min.' },
        noSoundMessage: { type: 'string', label: 'Message de silence', placeholder: 'Aucun son détecté' },
      },
    },
    emission: {
      type: 'object',
      label: 'Émission',
      description: 'Encodage et session du flux publié par cette source',
      properties: {
        streamParams: emissionStreamParamsSchema,
        sessionOptions: emissionSessionOptionsSchema,
      },
    },
    controls: {
      type: 'object',
      label: 'Contrôles PTZ (source)',
      description: 'MeetingSourceControls',
      properties: {
        type: { type: 'string', label: 'Type', default: 'aver-ptz', required: true, placeholder: 'aver-ptz' },
        hostname: { type: 'string', label: 'Hostname', placeholder: '192.168.1.x' },
        username: { type: 'string', label: 'Nom d\'utilisateur' },
        password: { type: 'string', label: 'Mot de passe' },
        entryPreset: { type: 'number', label: 'Preset d\'entrée', step: 1 },
        exitPreset: { type: 'number', label: 'Preset de sortie', step: 1 },
      },
    },
  },
});

const secondaryScreenColumnSchema: SchemaField = {
  type: 'object',
  label: 'Colonne',
  properties: {
    contentType: {
      type: 'select',
      label: 'Type de contenu',
      required: true,
      options: [
        { label: 'Lecteurs vidéo', value: 'players' },
        { label: 'Chat', value: 'chat' },
        { label: 'Utilisateurs', value: 'users' },
      ],
    },
    videoContainerId: { type: 'string', label: 'ID conteneur vidéo', description: 'Requis si contentType = "players"' },
    playerRows: { type: 'number', label: 'Lignes', min: 1, step: 1 },
    playerColumns: { type: 'number', label: 'Colonnes', min: 1, step: 1 },
    playerBoxPreset: {
      type: 'select',
      label: 'Preset',
      options: [
        { label: 'Par défaut', value: 'default' },
        { label: 'Audio', value: 'audio' },
        { label: 'Grille seule', value: 'gridonly' },
      ],
    },
    style: {
      type: 'json',
      expect: 'object',
      label: 'Style (JSON CSS)',
      description: 'Ex: { "width": "75%", "height": "100%" }',
      placeholder: '{ "width": "25%", "height": "100%" }',
    },
    playerStyle: {
      type: 'json',
      expect: 'object',
      label: 'Style des players (JSON CSS)',
      placeholder: '{ "borderRadius": 0 }',
    },
    overlayMode: {
      type: 'select',
      label: 'Mode overlay des players',
      description: 'Contrôle l\'affichage du header des players',
      options: [
        { label: 'Overlay (par défaut)', value: 'overlay' },
        { label: 'Overlay au survol', value: 'overlay-hoverable' },
        { label: 'Normal (au-dessus)', value: 'normal' },
      ],
    },
    singlePlayerMode: {
      type: 'boolean',
      label: 'Mode player unique',
      description: 'Force 1 colonne / 1 ligne et affiche l\'indicateur de page en overlay',
    },
  },
};

const secondaryScreenVariantSchema: SchemaField = {
  type: 'object',
  label: 'Variante',
  properties: {
    match: { type: 'string', label: 'Condition (match)', description: 'Expression de correspondance appliquée au contexte (meeting, carouselIds)' },
    layoutStyle: {
      type: 'json',
      expect: 'object',
      label: 'Style du layout (JSON CSS)',
      placeholder: '{ "padding": "2em" }',
    },
    justifyContent: {
      type: 'select',
      label: 'Justify Content',
      options: [
        { label: 'flex-start', value: 'flex-start' },
        { label: 'center', value: 'center' },
        { label: 'flex-end', value: 'flex-end' },
        { label: 'space-between', value: 'space-between' },
        { label: 'space-around', value: 'space-around' },
        { label: 'space-evenly', value: 'space-evenly' },
      ],
    },
    topBar: {
      type: 'object',
      label: 'Barre supérieure',
      properties: {
        brand: { type: 'boolean', label: 'Marque' },
        watchers: { type: 'boolean', label: 'Spectateurs' },
        user: { type: 'boolean', label: 'Utilisateur' },
        timer: { type: 'boolean', label: 'Timer' },
        meetingName: { type: 'boolean', label: 'Nom du meeting' },
      },
    },
    columns: {
      type: 'array',
      label: 'Colonnes',
      items: secondaryScreenColumnSchema,
    },
  },
};

const secondaryScreenLayoutSchema: SchemaField = {
  type: 'object',
  label: 'Layout',
  properties: {
    id: { type: 'number', label: 'ID', description: 'Identifiant du layout (vide = layout par défaut)', step: 1 },
    videoContainers: {
      type: 'array',
      label: 'Conteneurs vidéo',
      description: 'Conteneurs logiques liés à un carousel et un groupe',
      items: {
        type: 'object',
        label: 'Conteneur',
        properties: {
          id: { type: 'string', label: 'ID', required: true },
          carouselId: { type: 'string', label: 'ID Carousel' },
          groupId: { type: 'string', label: 'ID Groupe' },
          capacity: { type: 'number', label: 'Capacité', required: true, min: 1, step: 1 },
        },
      },
    },
    variants: {
      type: 'array',
      label: 'Variantes',
      items: secondaryScreenVariantSchema,
    },
  },
};

// ---- Composition sub-schema (`/composition/:id` source walls) ----

const compositionSlotSchema: SchemaField = {
  type: 'object',
  label: 'Slot',
  description: 'Une source placée dans la disposition. "Source" accepte le nom d\'une source '
    + '(de la composition ou globale) ou un objet JSON la définissant sur place. En grille nommée '
    + 'le slot occupe une zone du template, en disposition libre il porte sa position en %.',
  properties: {
    source: {
      type: 'string',
      label: 'Source',
      required: true,
      multiline: true,
      description: 'Nom, ou objet JSON.',
      placeholder: 'cam-prof',
    },
    area: {
      type: 'string',
      label: 'Zone',
      description: 'Vide : 1re zone libre.',
      placeholder: 'a',
    },
    rect: {
      type: 'object',
      label: 'Position (disposition libre)',
      description: 'En % de la page. Requis en disposition libre, ignoré en grille nommée.',
      properties: {
        x: { type: 'number', label: 'X (%)', min: -100, max: 200, step: 1 },
        y: { type: 'number', label: 'Y (%)', min: -100, max: 200, step: 1 },
        width: { type: 'number', label: 'Largeur (%)', min: 1, max: 200, step: 1 },
        height: { type: 'number', label: 'Hauteur (%)', min: 1, max: 200, step: 1 },
      },
    },
    zIndex: { type: 'number', label: 'Superposition', description: 'Au-dessus des plus faibles.', step: 1 },
    label: { type: 'string', label: 'Légende', description: 'Vide : aucune.' },
    fit: {
      type: 'select',
      label: 'Affichage',
      description: 'Défaut : celui de la source.',
      options: [
        { label: 'Contain (image entière)', value: 'contain' },
        { label: 'Cover (remplir)', value: 'cover' },
        { label: 'Fill (étirer)', value: 'fill' },
        { label: 'None', value: 'none' },
        { label: 'Scale-down', value: 'scale-down' },
      ],
    },
    muted: {
      type: 'boolean',
      label: 'Couper le son',
      description: 'Décoché : le son passe.',
    },
    style: {
      type: 'json',
      expect: 'object',
      label: 'Style (JSON CSS)',
      placeholder: '{ "borderRadius": 8 }',
    },
  },
};

const compositionSchema: SchemaField = {
  type: 'object',
  label: 'Composition',
  properties: {
    id: { type: 'string', label: 'Identifiant', description: 'URL : /composition/<id>', placeholder: 'regie' },
    name: { type: 'string', label: 'Nom' },
    background: { type: 'string', label: 'Fond', description: 'Couleur CSS. Défaut : noir.', placeholder: '#000000' },
    sources: {
      type: 'array',
      label: 'Sources de la composition',
      description: 'Sources utilisables par les slots de cette composition uniquement. '
        + 'Contrairement aux sources globales, elles ne sont jamais publiées en meeting.',
      items: meetingSourceSchema('Source'),
    },
    layout: {
      type: 'object',
      label: 'Disposition',
      description: 'Template : une ligne par rangée, cellules séparées par des espaces, "." pour une '
        + 'cellule vide (une rangée trop courte est complétée). Chaque zone doit rester rectangulaire. '
        + 'Un tableau JSON de lignes est accepté. Largeurs/hauteurs : valeurs CSS grid-template-*, '
        + 'égales par défaut.',
      // Deliberately flat rather than discriminated on `type`: these settings are
      // mostly hand-written, and a discriminator would silently drop `template`
      // from a layout that left `type` out (its default).
      properties: {
        type: {
          type: 'select',
          label: 'Type',
          description: 'Défaut : grille nommée.',
          options: [
            { label: 'Grille nommée (grid-template)', value: 'grid-template' },
            { label: 'Libre (positions en %)', value: 'free' },
          ],
        },
        template: {
          type: 'string',
          label: 'Template',
          multiline: true,
          description: 'Grille nommée.',
          placeholder: 'a a a\nb b c',
        },
        columns: { type: 'string', label: 'Largeurs', placeholder: '2fr 1fr 1fr' },
        rows: { type: 'string', label: 'Hauteurs', placeholder: '60% 40%' },
        gap: { type: 'number', label: 'Espacement (px)', min: 0, step: 1 },
        style: {
          type: 'json',
          expect: 'object',
          label: 'Style du conteneur (JSON CSS)',
          placeholder: '{ "padding": "1em" }',
        },
      },
    },
    slots: {
      type: 'array',
      label: 'Slots',
      description: 'Les sources affichées, dans l\'ordre. Un slot sans zone prend la première zone '
        + 'libre du template.',
      items: compositionSlotSchema,
    },
  },
};

// ---- Full settings schema ----

export const settingsSchema: SettingsFormSchema = {
  properties: {
    secret: {
      type: 'object',
      label: 'Sécurité',
      description: 'Mots de passe et codes PIN',
      properties: {
        adminPin: { type: 'string', label: 'PIN administrateur', placeholder: 'Code PIN' },
        localPassword: { type: 'string', label: 'Mot de passe local', placeholder: 'Mot de passe' },
        remotePassword: { type: 'string', label: 'Mot de passe distant', placeholder: 'Mot de passe' },
      },
    },
    roomId: { type: 'string', label: 'ID de salle', description: 'Identifiant de la salle de réunion', placeholder: 'ex: room-123' },
    dev: { type: 'boolean', label: 'Mode développeur', description: 'Active le mode développeur' },
    vod: {
      type: 'object',
      label: 'Replay (VOD)',
      description: 'Comportement du lecteur de replays',
      properties: {
        sequentialTileMounts: {
          type: 'boolean',
          label: 'Montage séquentiel des players',
          description: "Construit les player vidéo séquentiellement.",
        },
        engines: {
          type: 'object',
          label: 'Moteurs de lecture',
          properties: {
            webcodecs: {
              type: 'select',
              label: 'WebCodecs',
              description: 'Moteur de décodage du replay.',
              default: 'apple',
              options: [
                { label: 'Appareils Apple uniquement (défaut)', value: 'apple' },
                { label: 'Activé', value: 'true' },
                { label: 'Désactivé', value: 'false' },
              ],
            },
            mediaElement: { type: 'boolean', label: 'Éléments média' },
            disableWebcodecsDecoderPool: {
              type: 'boolean',
              label: 'Désactiver le pool de décodeurs WebCodecs',
              description: 'Recrée un décodeur vidéo à chaque seek et changement de clip au lieu de le réutiliser (comportement historique, pour comparer).',
            },
          },
        },
      },
    },
    logs: {
      type: 'object',
      label: 'Logs',
      description: 'Activation des logs par domaine',
      properties: {
        main: { type: 'boolean', label: 'Logs applicatifs' },
        mediaDiagnostics: {
          type: 'boolean',
          label: 'Diagnostic média',
          description: 'Journal passif caméra/micro (persisté — `__mediaDiag.dumpConsole()`)',
        },
        meetingContext: { type: 'boolean', label: 'Contexte meeting' },
        vodPlayer: {
          type: 'boolean',
          label: 'Player VOD',
          description: 'Journal interne du lecteur de replays (stalls, seeks, resync, retries)',
        },
        ovenMediaSession: {
          type: 'object',
          label: 'Sessions OvenMedia',
          properties: {
            reception: {
              type: 'object',
              label: 'Réception',
              properties: {
                main: { type: 'boolean', label: 'Logs' },
                webrtc: { type: 'boolean', label: 'Logs WebRTC' },
              },
            },
            emission: {
              type: 'object',
              label: 'Émission',
              properties: {
                main: { type: 'boolean', label: 'Logs' },
                webrtc: { type: 'boolean', label: 'Logs WebRTC' },
              },
            },
            retransmission: {
              type: 'object',
              label: 'Retransmission',
              properties: {
                main: { type: 'boolean', label: 'Logs' },
                webrtc: { type: 'boolean', label: 'Logs WebRTC' },
              },
            },
          },
        },
      },
    },
    soundIndicators: { type: 'boolean', label: 'Indicateurs sonores', description: 'Affiche les indicateurs de volume audio' },
    ui: {
      type: 'object',
      label: 'Interface',
      description: "Options d'affichage",
      properties: {
        hideLogoutButton: { type: 'boolean', label: 'Masquer le bouton déconnexion' },
        hideCloseAppButton: {
          type: 'boolean',
          label: "Masquer le bouton fermer l'application",
          description: "Masque le bouton même si l'application hôte autorise la fermeture.",
        },
        forceCloseAppButton: {
          type: 'boolean',
          label: "Forcer le bouton fermer l'application",
          description: "Affiche le bouton sans attendre que l'application hôte annonce qu'elle peut se fermer.",
        },
        hideOpenSettingsButton: {
          type: 'boolean',
          label: "Masquer le bouton paramètres de l'application",
          description: "Masque le bouton même si l'application hôte autorise l'ouverture de ses paramètres.",
        },
        forceOpenSettingsButton: {
          type: 'boolean',
          label: "Forcer le bouton paramètres de l'application",
          description: "Affiche le bouton sans attendre que l'application hôte annonce qu'elle peut ouvrir ses paramètres.",
        },
        showExpectedRoleBadge: { type: 'boolean', label: 'Afficher le rôle dans la liste des évènements' },
        showFullscreenButton: { type: 'boolean', label: 'Afficher le bouton plein écran dans la barre du haut' },
        showAgendaDatePicker: {
          type: 'boolean',
          label: 'Afficher le sélecteur de date des évènements',
          description: "Permet de consulter l'agenda d'un autre jour que celui en cours.",
        },
        disableMeetingList: {
          type: 'boolean',
          label: 'Masquer la liste des meetings',
          description: "Mode LMS. On ne renvoie pas sur la liste.",
        },
        disableTimeClamping: {
          type: 'boolean',
          label: 'Désactiver le clamping des compteurs',
          description: "Le clamping permet aux compteurs de temps écoulé de ne pas rester bloqués sur 00:00 quand ils rencontrent une date serveur dans le futur.",
        },
        enableTimelineZoom: {
          type: 'boolean',
          label: 'Loupe de la timeline (replay)',
          description: 'Un appui long zoome la timeline.',
        },
        minimapPan: {
          type: 'select',
          label: 'Déplacement des minimaps',
          description: 'Horizontal seul, ou zoom et déplacement libre.',
          options: [
            { label: 'Horizontal', value: 'horizontal' },
            { label: 'Libre (zoom)', value: 'free' },
          ],
        },
        grid: {
          type: 'object',
          label: 'Grille',
          description: 'Disposition des lecteurs en mode grille (plein)',
          properties: {
            mode: {
              type: 'select',
              label: 'Disposition',
              description: 'Adaptative : maximise la place. Templates : gabarits fixes (1 à 9).',
              options: [
                { label: 'Adaptative', value: 'adaptive' },
                { label: 'Templates', value: 'template' },
              ],
            },
            pageSize: {
              type: 'number',
              label: 'Lecteurs par page',
              description: 'Laissez vide pour automatique (selon la taille/densité de l\'écran). Limité à 9 en mode Templates.',
              min: 1,
              step: 1,
            },
            autoTileMinPx: {
              type: 'number',
              label: 'Taille mini de tuile (px)',
              description: 'Utilisé en mode automatique pour garder des tuiles lisibles.',
              min: 60,
              step: 10,
            },
          },
        },
      },
    },
    bridge: {
      type: 'object',
      label: 'Bridge (messagerie)',
      description: 'Configuration du pont de communication master/slave',
      properties: {
        master: {
          type: 'object',
          label: 'Master',
          properties: {
            enableBroadcast: { type: 'boolean', label: 'Activer le broadcast' },
          },
        },
        slave: {
          type: 'object',
          label: 'Slave',
          properties: {
            enableBroadcast: { type: 'boolean', label: 'Activer le broadcast' },
            enableWebsocket: { type: 'boolean', label: 'Activer le WebSocket' },
            websocketEndpoint: { type: 'string', label: 'Endpoint WebSocket', placeholder: 'wss://...' },
          },
        },
      },
    },
    sources: {
      type: 'array',
      label: 'Sources',
      description: 'Sources média (caméras, écrans, etc.)',
      items: meetingSourceSchema('Source'),
    },
    compositions: {
      type: 'array',
      label: 'Compositions',
      description: 'Murs de sources affichés par la page /composition/<id> : des sources disposées '
        + 'en plein écran, hors de tout meeting.',
      items: compositionSchema,
    },
    videoFeedback: {
      type: 'object',
      label: 'Retour vidéo',
      description: 'Source utilisée pour le retour vidéo',
      properties: {
        source: meetingSourceSchema('Source'),
      },
    },
    webcamFeedback: {
      type: 'object',
      label: 'Retour webcam',
      description: 'Source utilisée pour le retour webcam',
      properties: {
        source: meetingSourceSchema('Source'),
      },
    },
    debug: {
      type: 'object',
      label: 'Debug',
      description: 'Options de débogage',
      properties: {
        displaySessionIds: { type: 'boolean', label: 'Afficher les IDs de session' },
        displayIncomingStreamStats: { type: 'boolean', label: 'Stats flux entrants' },
        displayOutgoingStreamStats: { type: 'boolean', label: 'Stats flux sortants' },
        audio: {
          type: 'object',
          label: 'Audio',
          properties: {
            mixer: {
              type: 'object',
              label: 'Mixer',
              properties: {
                public: { type: 'boolean', label: 'Public' },
                useMediaStreamDestination: { type: 'boolean', label: 'Utiliser MediaStreamDestination' },
                disableAutoResumeAudioContext: { type: 'boolean', label: 'Désactiver auto-resume AudioContext' },
                equalizer: { type: 'boolean', label: 'Égaliseur' },
                notUseCompressor: { type: 'boolean', label: 'Désactiver le compresseur' },
              },
            },
          },
        },
        sdkClient: {
          type: 'object',
          label: 'SDK Client',
          properties: {
            public: { type: 'boolean', label: 'Public' },
          },
        },
        meetingContext: {
          type: 'object',
          label: 'Meeting Context',
          properties: {
            public: { type: 'boolean', label: 'Public' },
          },
        },
      },
    },
    emission: {
      type: 'object',
      label: 'Émission',
      description: 'Configuration de l\'émission WebRTC',
      properties: {
        sdpFormatter: {
          type: 'object',
          label: 'SDP Formatter',
          description: 'Pipelines SdpFormatter appliqués aux SDP émis. Actions séparées par `|`, '
            + 'sans espace autour du séparateur (il compte dans le nom de l\'action), arguments en JSON : '
            + 'set_bitrate_limit["video", 2500000]|remove_lines_from_media["video", "a=extmap:.*video-orientation"]. '
            + 'Un pipeline invalide est refusé à la sauvegarde.',
          properties: {
            offer: { type: 'string', label: 'Offer', multiline: true, placeholder: 'set_bitrate_limit["video", 2500000]' },
            answer: { type: 'string', label: 'Answer', multiline: true, placeholder: 'remove_format[103]' },
          },
        },
      },
    },
    retransmission: {
      type: 'object',
      label: 'Retransmission',
      description: 'Republication des flux du meeting vers des URLs OvenMedia (record, restream...)',
      properties: {
        destinations: {
          type: 'array',
          label: 'Destinations',
          description: 'Chaque destination matche des flux (matchEngine) et les republie vers une URL',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', label: 'ID', description: 'Identifiant stable de la destination (le changer relance la publication)', required: true },
              match: {
                type: 'string',
                label: 'Condition (matchEngine)',
                description: 'CTX : type (emission|reception|custom), key, session, media, currentSession, meeting, highlighted',
                required: true,
              },
              url: {
                type: 'string',
                label: 'URL',
                description: 'Même CTX : {{example.path,defaut}} + nonce, attempt, attemptAt',
                required: true,
              },
              srcKey: {
                type: 'string',
                label: 'Clé de cible (optionnel)',
                description: 'Template (même ctx que l\'URL) : quand sa valeur change, la republication est relancée vers la nouvelle cible',
              },
              audio: { type: 'boolean', label: 'Audio', description: 'Republie les pistes audio (défaut : oui)' },
              video: { type: 'boolean', label: 'Vidéo', description: 'Republie les pistes vidéo (défaut : oui)' },
              cloneTracks: {
                type: 'boolean',
                label: 'Cloner les pistes',
                description: 'Défaut : oui — la republication survit aux mute / pause locaux de la source. Non : elle les suit',
              },
              fillMissingAudioTrack: {
                type: 'boolean',
                label: 'Compléter l\'audio manquant',
                description: 'Ajoute une piste audio silencieuse si la source n\'en a pas (OvenMedia refuse les publications mono-type)',
              },
              fillMissingVideoTrack: {
                type: 'boolean',
                label: 'Compléter la vidéo manquante',
                description: 'Ajoute une piste vidéo noire si la source n\'en a pas',
              },
              earlyStart: {
                type: 'boolean',
                label: 'Démarrage anticipé',
                description:
                  'Négocie la publication avant l\'arrivée du flux source (pistes vides)',
              },
              bypassAudio: {
                type: 'boolean',
                label: 'Audio sans réencodage',
                description: 'Republie les trames audio reçues sans ré-encodage.',
              },
              bypassVideo: {
                type: 'boolean',
                label: 'Vidéo sans réencodage',
                description: 'Republie les trames vidéo reçues sans ré-encodage.',
              },
              sessionOptions: {
                type: 'json',
                expect: 'object',
                label: 'Options de session (JSON)',
                description: 'Options de l\'émetteur OvenMedia, fusionnées par-dessus celles du manager. Lues à la création de la publication',
                placeholder: '{ "video": { "googleBitrates": { "max": 2500000 } } }',
              },
              stopDelay: { type: 'number', label: 'Délai d\'arrêt (ms)', min: 0, step: 1000, description: 'Grâce avant arrêt quand le match ne s\'applique plus' },
            },
          },
        },
      },
    },
    mediaEvents: {
      type: 'object',
      label: 'Événements médias',
      description:
        'Ce que chaque flux publié par cette page — ses propres médias comme ceux qu\'elle republie — signale au middleware d\'enregistrement '
        + '(caméra coupée, micro coupé, image masquée, piste absente…), pour qu\'un replay explique une tuile noire',
      properties: {
        endpoint: {
          type: 'string',
          label: 'Route (optionnel)',
          description:
            'Base de la route du middleware qui reçoit les événements de chaque publication : <base>/<app>/<stream>. '
            + 'Vide : déduite de l\'hôte de publication (https://<hôte>/middleware/events/media). À renseigner quand cet hôte ne sert pas le middleware, '
            + 'ex. http://localhost:8088/events/media en dev local.',
        },
      },
    },
    carousel: {
      type: 'object',
      label: 'Carousel',
      description: 'Configuration du carousel des écrans secondaires',
      properties: {
        prelaodDelay: { type: 'number', label: 'Délai de préchargement (ms)', min: 0, step: 100 },
        interval: { type: 'number', label: 'Intervalle (ms)', min: 0, step: 1000 },
        layouts: {
          type: 'array',
          label: 'Layouts',
          description: 'Dispositions des écrans secondaires',
          items: secondaryScreenLayoutSchema,
        },
        filters: {
          type: 'array',
          label: 'Filtres',
          description: 'Filtres par carousel',
          items: {
            type: 'object',
            properties: {
              carouselId: { type: 'string', label: 'ID Carousel', required: true },
              value: { type: 'string', label: 'Valeur du filtre', required: true },
            },
          },
        },
      },
    },
    instantMeeting: {
      type: 'object',
      label: 'Meeting instantané',
      description: "Création de meetings à la volée depuis la liste",
      properties: {
        enabled: {
          type: 'boolean',
          label: 'Activer les meetings instantanés',
          description: "Masque le bouton de création et l'onglet « Instantanés ». Activé par défaut.",
          default: true,
        },
        privacy: {
          type: 'object',
          label: 'Confidentialité',
          description: "Une seule option proposée : elle est appliquée sans être affichée. Public et Privé par défaut.",
          properties: {
            public: {
              type: 'boolean',
              label: 'Proposer « Public »',
              description: 'Tout le monde peut rejoindre.',
              default: true,
            },
            private: {
              type: 'boolean',
              label: 'Proposer « Privé »',
              description: "Membres de l'organisation uniquement.",
              default: true,
            },
            closed: {
              type: 'boolean',
              label: 'Proposer « Restreint »',
              description: "Le créateur choisit qui peut rejoindre. Demande user:read:any + identity:read:any. Non proposé par défaut.",
            },
            default: {
              type: 'select',
              label: 'Option pré-sélectionnée',
              description: "Défaut : Public. Ignorée si l'option n'est pas proposée.",
              options: [
                { label: 'Public', value: 'public' },
                { label: 'Privé', value: 'private' },
                { label: 'Restreint', value: 'closed' },
              ],
            },
          },
        },
        speakingMode: {
          type: 'object',
          label: 'Prise de parole',
          description: "Une seule option proposée : elle est appliquée sans être affichée. Tout est proposé par défaut.",
          properties: {
            conference: {
              type: 'boolean',
              label: 'Proposer « Mode conférence »',
              description: 'Les arrivants sont spectateurs.',
              default: true,
            },
            free: {
              type: 'boolean',
              label: 'Proposer « Mode libre »',
              description: 'Tout le monde peut parler.',
              default: true,
            },
            default: {
              type: 'select',
              label: 'Option pré-sélectionnée',
              description: "Défaut : Mode conférence. Ignorée si l'option n'est pas proposée.",
              options: [
                { label: 'Mode conférence', value: 'conference' },
                { label: 'Mode libre', value: 'free' },
              ],
            },
          },
        },
        record: {
          type: 'object',
          label: 'Enregistrement',
          description: "Rien de proposé (défaut) : le réglage de l'organisation s'applique. Une seule option : elle est appliquée sans être affichée.",
          properties: {
            on: {
              type: 'boolean',
              label: 'Proposer « Enregistrer »',
              description: 'Enregistré et disponible en replay.',
            },
            off: {
              type: 'boolean',
              label: 'Proposer « Ne pas enregistrer »',
              description: "Le meeting n'est pas enregistré.",
            },
            default: {
              type: 'select',
              label: 'Option pré-sélectionnée',
              description: "Défaut : Ne pas enregistrer. Ignorée si l'option n'est pas proposée.",
              options: [
                { label: 'Enregistrer', value: true },
                { label: 'Ne pas enregistrer', value: false },
              ],
            },
          },
        },
      },
    },
    features: {
      type: 'object',
      label: 'Fonctionnalités',
      description: 'Activation et configuration des fonctionnalités',
      properties: {
        survey: { type: 'boolean', label: 'Sondages' },
        board: { type: 'boolean', label: 'Montée au tableau' },
        boardMatch: { type: 'string', label: 'Filtre board (matchEngine)' },
        audioMatch: { type: 'string', label: 'Filtre audio (matchEngine)' },
        rememberGuests: { type: 'boolean', label: 'Mémoriser les invités', description: 'Réutiliser les identités invité par réunion (reprise "en tant que...")' },
      },
    },
  },
};

// ---- Helpers ----

/**
 * Merge base properties with discriminator-mapped properties based on the actual data.
 * Mirrors the logic in SettingsForm's getEffectiveProperties().
 */
const getEffectiveProperties = (field: SchemaField & { type: 'object' }, obj: any): Record<string, SchemaField> => {
  if (!field.discriminator || typeof obj !== 'object' || obj === null) return field.properties;
  // Fall back to the disabled copy of the discriminator so a subtree whose
  // `type` is disabled in the form still resolves its `data` schema.
  const discVal = obj[field.discriminator.field] ?? obj[`_hidden_${field.discriminator.field}`];
  const extra = (discVal !== undefined && field.discriminator.mapping[String(discVal)]) || {};
  return { ...field.properties, ...extra };
};

/**
 * Resolve the schema field governing a `_hidden_*` data key, mirroring
 * SettingsForm's classifyDataKey(): `_hidden_items_<name>` maps to the items
 * of the array field <name> (when the schema has such an array — priority),
 * `_hidden_<key>` maps to the field <key>. Returns undefined when the key
 * matches nothing in the schema (opaque metadata, kept as-is by callers).
 */
const resolveHiddenField = (
  key: string,
  properties: Record<string, SchemaField>,
): { field: SchemaField; isItems: boolean } | undefined => {
  if (key.startsWith('_hidden_items_')) {
    const arrayName = key.slice('_hidden_items_'.length);
    const arrayField = properties[arrayName];
    if (arrayField?.type === 'array') return { field: arrayField.items, isItems: true };
  }
  const schemaKey = key.slice('_hidden_'.length);
  const field = properties[schemaKey];
  if (field) return { field, isItems: false };
  return undefined;
};

// ---- Sanitization ----

const getSchemaDefault = (field: SchemaField): any => {
  switch (field.type) {
    case 'string': return field.default ?? '';
    case 'number': return field.default ?? 0;
    case 'boolean': return field.default ?? false;
    case 'select': return field.default ?? (field.options.length > 0 ? field.options[0].value : '');
    case 'object': return {};
    case 'array': return field.default ?? [];
    case 'json': return field.default ?? '';
  }
};

const getExpectedType = (field: SchemaField): string => {
  switch (field.type) {
    case 'string': return 'string';
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    case 'select': return typeof (field.options[0]?.value ?? '');
    case 'object': return 'object';
    case 'array': return 'array';
    case 'json': return 'json'; // never used: sanitizeField handles 'json' before matchesType
  }
};

const matchesType = (value: any, field: SchemaField): boolean => {
  if (value === null || value === undefined) return true; // optional fields
  const expected = getExpectedType(field);
  if (expected === 'array') return Array.isArray(value);
  if (expected === 'object') return typeof value === 'object' && !Array.isArray(value);
  return typeof value === expected;
};

/**
 * Sanitize an imported settings object against the schema.
 * - Removes keys not in the schema
 * - Replaces values with wrong types by schema defaults
 * - Recursively walks objects and arrays
 * Returns { sanitized, warnings } where warnings list what was changed.
 */
/** Kept in sync with `substituteSettingsVariables` (helpers/settings.ts). */
const WHOLE_PLACEHOLDER_PATTERN = /^\[\[\s*[A-Za-z0-9_.-]+\s*\]\]$/;

export const sanitizeSettings = (
  data: any,
  schema: SettingsFormSchema,
): { sanitized: any; warnings: string[] } => {
  const warnings: string[] = [];

  const sanitizeObject = (obj: any, properties: Record<string, SchemaField>, path: string): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      warnings.push(`${path || 'racine'}: attendu un objet, remplacé par {}`);
      return {};
    }
    const result: any = {};

    // Remove unknown keys (but keep _hidden_ prefixed keys)
    for (const key of Object.keys(obj)) {
      if (!(key in properties) && !key.startsWith('_hidden_')) {
        warnings.push(`${path ? path + '.' : ''}${key}: champ inconnu`);
      }
    }

    // Validate known keys
    for (const [key, field] of Object.entries(properties)) {
      const fullPath = path ? `${path}.${key}` : key;
      if (!(key in obj) || obj[key] === undefined) continue; // optional, skip

      const value = obj[key];
      result[key] = sanitizeField(value, field, fullPath);
    }

    // Sanitize _hidden_ prefixed keys through their real schema field so
    // disabled subtrees are validated and coerced like visible ones.
    for (const key of Object.keys(obj)) {
      if (!key.startsWith('_hidden_')) continue;
      const fullPath = path ? `${path}.${key}` : key;
      const hidden = resolveHiddenField(key, properties);
      // _hidden_<field> is redundant when <field> already exists (but _hidden_items_<field> can coexist)
      if (hidden && !hidden.isItems) {
        const visibleKey = key.slice('_hidden_'.length);
        if (visibleKey in result) {
          warnings.push(`${path ? path + '.' : ''}${key}: ignoré car ${visibleKey} existe déjà`);
          continue;
        }
      }
      if (!hidden) {
        result[key] = obj[key]; // opaque metadata — keep as-is
      } else if (hidden.isItems) {
        const items = Array.isArray(obj[key]) ? obj[key] : [];
        result[key] = items.map((item: any, i: number) => sanitizeField(item, hidden.field, `${fullPath}[${i}]`));
      } else {
        result[key] = sanitizeField(obj[key], hidden.field, fullPath);
      }
    }

    return result;
  };

  const sanitizeField = (value: any, field: SchemaField, path: string): any => {
    if (value === null || value === undefined) return value;

    // A whole-value `[[NAME]]` placeholder stands for whatever the field
    // expects: it is validated after substitution, not here. Without this, a
    // template would lose every placeholder sitting in a typed field.
    if (typeof value === 'string' && WHOLE_PLACEHOLDER_PATTERN.test(value)) return value;

    // JSON fields: output form shape (JSON text), validate content and `expect`.
    if (field.type === 'json') {
      const fail = (msg: string) => {
        warnings.push(`${path}: ${msg}, remplacé par la valeur par défaut`);
        return getSchemaDefault(field);
      };
      let parsed: any = value;
      if (typeof value === 'string') {
        if (value.trim() === '') return value;
        try { parsed = JSON.parse(value); } catch { return fail('JSON invalide'); }
      }
      if (field.expect === 'object' && (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))) {
        return fail('objet JSON attendu');
      }
      if (field.expect === 'array' && !Array.isArray(parsed)) {
        return fail('tableau JSON attendu');
      }
      if (typeof value === 'string') return value; // keep the user's formatting
      try { return JSON.stringify(parsed, null, 2); } catch { return fail('valeur non sérialisable'); }
    }

    // Check for JSON string fields (data, extra, style in our schema are strings that hold JSON)
    if (field.type === 'string') {
      if (typeof value === 'object') {
        // An object where we expect a JSON string — convert it
        try {
          return JSON.stringify(value, null, 2);
        } catch {
          warnings.push(`${path}: objet non sérialisable, remplacé par la valeur par défaut`);
          return getSchemaDefault(field);
        }
      }
      if (typeof value !== 'string') {
        warnings.push(`${path}: attendu string, reçu ${typeof value}, remplacé par la valeur par défaut`);
        return getSchemaDefault(field);
      }
      return value;
    }

    if (!matchesType(value, field)) {
      warnings.push(`${path}: attendu ${getExpectedType(field)}, reçu ${Array.isArray(value) ? 'array' : typeof value}, remplacé par la valeur par défaut`);
      return getSchemaDefault(field);
    }

    if (field.type === 'object') {
      return sanitizeObject(value, getEffectiveProperties(field, value), path);
    }

    if (field.type === 'array') {
      if (!Array.isArray(value)) return getSchemaDefault(field);
      return value.map((item, i) => sanitizeField(item, field.items, `${path}[${i}]`));
    }

    return value;
  };

  const sanitized = sanitizeObject(data, schema.properties, '');
  return { sanitized, warnings };
};

// ---- Prepare for form (silent coercion, no warnings) ----

/**
 * Coerce settings values to match schema expectations before feeding to SettingsForm.
 * Mainly converts JS objects to JSON strings where the schema declares a string field.
 */
export const prepareSettingsForForm = (data: any, schema: SettingsFormSchema): any => {
  const coerceObject = (obj: any, properties: Record<string, SchemaField>): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return {};
    const result: any = {};
    for (const [key, field] of Object.entries(properties)) {
      if (!(key in obj) || obj[key] === undefined) continue;
      result[key] = coerceField(obj[key], field);
    }
    // Coerce _hidden_ prefixed keys through their real schema field so
    // disabled subtrees reach the form in form shape (JSON objects → strings).
    for (const key of Object.keys(obj)) {
      if (!key.startsWith('_hidden_')) continue;
      const hidden = resolveHiddenField(key, properties);
      if (hidden && !hidden.isItems) {
        const visibleKey = key.slice('_hidden_'.length);
        if (visibleKey in result) continue; // redundant with the visible field
      }
      if (!hidden) {
        result[key] = obj[key]; // opaque metadata — keep as-is
      } else if (hidden.isItems) {
        const items = Array.isArray(obj[key]) ? obj[key] : [];
        result[key] = items.map((item: any) => coerceField(item, hidden.field));
      } else {
        result[key] = coerceField(obj[key], hidden.field);
      }
    }
    return result;
  };

  const coerceField = (value: any, field: SchemaField): any => {
    if (value === null || value === undefined) return value;
    // JSON fields are edited as text: parsed values become JSON strings.
    if (field.type === 'json' && typeof value !== 'string') {
      try { return JSON.stringify(value, null, 2); } catch { return ''; }
    }
    if (field.type === 'string' && typeof value === 'object') {
      try { return JSON.stringify(value, null, 2); } catch { return ''; }
    }
    if (field.type === 'object') return coerceObject(value, getEffectiveProperties(field, value));
    if (field.type === 'array' && Array.isArray(value)) {
      return value.map((item) => coerceField(item, field.items));
    }
    return value;
  };

  return coerceObject(data, schema.properties);
};

// ---- Restore from form (reverse of prepareSettingsForForm) ----

/**
 * Convert multiline JSON string fields back to objects after reading from SettingsForm.
 * Targets `type: 'string', multiline: true` fields that may hold serialized JSON.
 */
export const restoreSettingsFromForm = (data: any, schema: SettingsFormSchema): any => {
  const restoreObject = (obj: any, properties: Record<string, SchemaField>): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return obj;
    const result: any = {};
    for (const key of Object.keys(obj)) {
      if (key in properties) {
        result[key] = restoreField(obj[key], properties[key]);
        continue;
      }
      // _hidden_ prefixed keys hold disabled fields / hidden array items in
      // form shape — restore them through their real schema field, otherwise
      // their JSON strings (e.g. web children) leak into the persisted settings.
      const hidden = key.startsWith('_hidden_') ? resolveHiddenField(key, properties) : undefined;
      if (!hidden) {
        result[key] = obj[key];
      } else if (hidden.isItems) {
        const items = Array.isArray(obj[key]) ? obj[key] : [];
        result[key] = items.map((item: any) => restoreField(item, hidden.field));
      } else {
        result[key] = restoreField(obj[key], hidden.field);
      }
    }
    return result;
  };

  const restoreField = (value: any, field: SchemaField): any => {
    if (value === null || value === undefined) return value;
    // JSON field → parse the text back to its value. An empty editor means
    // "field absent"; invalid JSON cannot be saved from the form (blocking
    // error) — if some slips in from elsewhere it is kept visible as-is.
    if (field.type === 'json' && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return undefined;
      try { return JSON.parse(trimmed); } catch { return value; }
    }
    // Multiline string fields may hold either free text or JSON (e.g. device
    // selectors: plain deviceId string OR selector object) → parse when it
    // looks like JSON.
    if (field.type === 'string' && (field as any).multiline && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try { return JSON.parse(trimmed); } catch { /* keep as string */ }
      }
      return value;
    }
    if (field.type === 'object') return restoreObject(value, getEffectiveProperties(field, value));
    if (field.type === 'array' && Array.isArray(value)) {
      return value.map((item) => restoreField(item, field.items));
    }
    return value;
  };

  return restoreObject(data, schema.properties);
};

// ---- Export ----

export const exportSettings = (settings: any) => {
  const exported = {
    _schemaVersion: SETTINGS_SCHEMA_VERSION,
    ...settings,
  };
  const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `settings-v${SETTINGS_SCHEMA_VERSION}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ---- Import ----

/** localhost and the private ranges — trusted origins, but subject to Private Network Access. */
const isPrivateNetworkHost = (hostname: string): boolean => (
  /^(localhost|127\.\d+\.\d+\.\d+|\[?::1\]?)$/i.test(hostname)
  || /^10\.\d+\.\d+\.\d+$/.test(hostname)
  || /^192\.168\.\d+\.\d+$/.test(hostname)
  || /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(hostname)
  || /^169\.254\.\d+\.\d+$/.test(hostname)
  || hostname.endsWith('.local')
);

/** Boot waits on these fetches: never let one hang the app for longer than this. */
export const SETTINGS_FETCH_TIMEOUT_MS = 8000;

/** A base64 payload is a single unbroken token — anything else is a document. */
const LOOKS_LIKE_BASE64 = /^[A-Za-z0-9+/\-_]+={0,2}$/;

/** Names what was received, so a wrong URL reads as such instead of "invalid JSON". */
const describeContent = (text: string): string => {
  const head = text.trimStart().slice(0, 400).toLowerCase();
  if (head.startsWith('<!doctype html') || head.startsWith('<html') || head.includes('<body')) {
    return 'Le contenu reçu est une page HTML (URL erronée, page d\'erreur ou redirection du serveur ?) et';
  }
  if (head.startsWith('<')) return 'Le contenu reçu est un document XML/HTML et';
  return 'Le contenu reçu';
};

export interface ImportResult {
  settings: any;
  warnings: string[];
  versionWarning?: string;
}

export interface ParsedSettingsSource {
  /** Settings exactly as written, `_schemaVersion` aside — nothing coerced or dropped. */
  raw: any;
  versionWarning?: string;
}

/**
 * Parse a settings payload (JSON, or base64/base64url-encoded JSON — the forms
 * accepted by the `?settings=` URL query) WITHOUT validating it against the
 * schema. Remote configs need this: they may hold `[[VARIABLE]]` placeholders
 * in typed fields, which the sanitizer would coerce away before substitution.
 * Throws on anything that is not a settings object.
 */
export const parseSettingsSource = (text: string): ParsedSettingsSource => {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Contenu vide');

  let raw: any;
  try {
    raw = JSON.parse(trimmed);
  } catch (jsonErr) {
    // base64 payloads are one long token: a content that looks like a document
    // (HTML, YAML, a JSON with a typo) is reported as the JSON error it is,
    // rather than as an unhelpful "not base64 either".
    const decoded = LOOKS_LIKE_BASE64.test(trimmed) ? decodeBase64Url(trimmed) : undefined;
    if (decoded === undefined) {
      throw new Error(
        `${describeContent(trimmed)} n'est pas un JSON valide :\n${formatJsonParseError(jsonErr, trimmed)}`,
      );
    }
    try {
      raw = JSON.parse(decoded);
    } catch (err) {
      throw new Error('Le contenu base64 décodé n\'est pas un JSON valide :\n' + formatJsonParseError(err, decoded));
    }
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`Le JSON doit être un objet de paramètres (reçu : ${Array.isArray(raw) ? 'un tableau' : typeof raw})`);
  }

  const { _schemaVersion, ...data } = raw;

  let versionWarning: string | undefined;
  if (_schemaVersion !== undefined && _schemaVersion !== SETTINGS_SCHEMA_VERSION) {
    if (_schemaVersion > SETTINGS_SCHEMA_VERSION) {
      versionWarning = `Ces paramètres proviennent d'un schéma plus récent (v${_schemaVersion} vs v${SETTINGS_SCHEMA_VERSION} actuelle). Certains champs pourraient ne pas être reconnus.`;
    } else {
      versionWarning = `Ces paramètres proviennent d'un schéma plus ancien (v${_schemaVersion} vs v${SETTINGS_SCHEMA_VERSION} actuelle). Certains champs pourraient être manquants ou obsolètes.`;
    }
  } else if (_schemaVersion === undefined) {
    versionWarning = `Ces paramètres ne contiennent pas de version de schéma. Ils pourraient ne pas être compatibles avec la version actuelle (v${SETTINGS_SCHEMA_VERSION}).`;
  }

  return { raw: data, versionWarning };
};

/**
 * Import settings from raw text: parses it, then validates against the schema
 * (unknown fields dropped, values coerced — reported as warnings).
 */
export const importSettingsFromText = (text: string): ImportResult => {
  const { raw, versionWarning } = parseSettingsSource(text);
  const { sanitized, warnings } = sanitizeSettings(raw, settingsSchema);
  return { settings: sanitized, warnings, versionWarning };
};

/**
 * Import settings from a URL. If the URL itself carries a `?settings=` payload
 * (an app link), the payload is imported directly without any network request;
 * otherwise the URL is fetched and its body parsed as JSON / base64 JSON.
 */
export interface FetchedSettingsSource extends ParsedSettingsSource {
  /** Absolute URL actually fetched (relative inputs resolved against the app). */
  href: string;
}

/**
 * Fetch a settings URL and parse it, without schema validation. If the URL
 * carries a `?settings=` payload (an app link), that payload is used directly
 * and no request is made. Throws with a message naming what went wrong:
 * unreachable host, HTTP status, non-JSON body.
 */
export const fetchSettingsSource = async (url: string): Promise<FetchedSettingsSource> => {
  const trimmed = url.trim();
  if (!trimmed) throw new Error('URL vide');

  let target: URL;
  try {
    // Base for relative paths; also extracts `?settings=` from absolute app links.
    target = new URL(trimmed, window.location.href);
  } catch (err) {
    throw new Error(`URL invalide : « ${trimmed} » n'est pas une URL exploitable`);
  }
  const payload = target.searchParams.get('settings');
  if (payload) return { ...parseSettingsSource(payload), href: target.href };

  let response: Response;
  const controller = new AbortController();
  // The app waits for this fetch before it renders: an unreachable host would
  // otherwise hang on the TCP timeout (tens of seconds) with nothing on screen.
  const timeout = setTimeout(() => controller.abort(), SETTINGS_FETCH_TIMEOUT_MS);
  try {
    // no-store: a settings URL is re-fetched to get the current content, never
    // whatever the browser cached on a previous launch.
    response = await fetch(target.href, { cache: 'no-store', signal: controller.signal });
  } catch (err) {
    if ((err as any)?.name === 'AbortError') {
      throw new Error(
        `${target.href} n'a pas répondu en ${SETTINGS_FETCH_TIMEOUT_MS / 1000} s.\n`
        + '- vérifiez que le serveur est démarré et joignable depuis ce poste\n'
        + '- une adresse injoignable (VPN coupé, mauvais port) reste sans réponse jusqu\'à expiration',
      );
    }
    // The browser never says why a fetch failed (server down, DNS, mixed
    // content, CORS, private network): all of them surface as "Failed to fetch".
    // List what it can be, starting with the one the URL itself makes likely.
    const reasons: string[] = [];
    if (target.origin !== window.location.origin) {
      reasons.push(
        `la requête est inter-origine (page servie depuis ${window.location.origin}, fichier sur ${target.origin}) : `
        + 'le serveur qui héberge le fichier doit renvoyer l\'en-tête « Access-Control-Allow-Origin », sinon le '
        + 'navigateur bloque la réponse — un test dans la console depuis un onglet déjà ouvert sur ce serveur ne '
        + 'prouve rien, il est de même origine (réponse « type: basic »)',
      );
    }
    if (isPrivateNetworkHost(target.hostname) && !isPrivateNetworkHost(window.location.hostname)) {
      reasons.push(
        'la page est publique et la cible est sur le réseau local (Private Network Access) : le navigateur envoie '
        + 'un préflight que le serveur doit accepter avec « Access-Control-Allow-Private-Network: true »',
      );
    }
    // http://localhost & co. are trusted origins: they are not mixed content.
    if (target.protocol === 'http:' && window.location.protocol === 'https:' && !isPrivateNetworkHost(target.hostname)) {
      reasons.push('l\'application est en HTTPS et l\'URL en HTTP : le navigateur bloque le contenu mixte');
    }
    reasons.push('le serveur peut aussi être arrêté, injoignable ou avoir un certificat refusé');
    throw new Error(
      `Impossible de contacter ${target.href} (${(err as any)?.message ?? err}).\n`
      + reasons.map((r) => `- ${r}`).join('\n'),
    );
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    let excerpt = '';
    try {
      const body = (await response.text()).trim();
      if (body) excerpt = `\nRéponse : ${body.slice(0, 200)}${body.length > 200 ? '…' : ''}`;
    } catch (err) { /* body already consumed or unreadable — the status is enough */ }
    throw new Error(
      `${target.href} a répondu HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}.`
      + `${response.status === 404 ? ' Le chemin du fichier est probablement erroné.' : ''}${excerpt}`,
    );
  }

  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  try {
    return { ...parseSettingsSource(text), href: target.href };
  } catch (err) {
    const type = contentType ? ` (Content-Type : ${contentType.split(';')[0]})` : '';
    throw new Error(`${target.href}${type}\n${(err as Error).message}`);
  }
};

/**
 * Import settings from a URL: fetches, parses, then validates against the schema.
 */
export const importSettingsFromUrl = async (url: string): Promise<ImportResult> => {
  const { raw, versionWarning } = await fetchSettingsSource(url);
  const { sanitized, warnings } = sanitizeSettings(raw, settingsSchema);
  return { settings: sanitized, warnings, versionWarning };
};

export const importSettings = (file: File): Promise<ImportResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        resolve(importSettingsFromText(e.target?.result as string));
      } catch (err) {
        reject(new Error('Fichier invalide : ' + (err as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Impossible de lire le fichier'));
    reader.readAsText(file);
  });
};
